import { useSyncExternalStore } from "react";

const KEY = "yarn_muted";
let ctx = null;
let muted = localStorage.getItem(KEY) === "1";
const listeners = new Set();

function audio() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

function meow() {
  const c = !muted && audio();
  if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(430, t);
  osc.frequency.linearRampToValueAtTime(840, t + 0.18);
  osc.frequency.linearRampToValueAtTime(620, t + 0.45);
  osc.frequency.linearRampToValueAtTime(390, t + 0.78);
  const lfo = c.createOscillator();
  const lfoGain = c.createGain();
  lfo.frequency.value = 7;
  lfoGain.gain.value = 14;
  lfo.connect(lfoGain).connect(osc.frequency);
  const filter = c.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = 3.5;
  filter.frequency.setValueAtTime(900, t);
  filter.frequency.linearRampToValueAtTime(2300, t + 0.2);
  filter.frequency.linearRampToValueAtTime(850, t + 0.78);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.4, t + 0.06);
  gain.gain.setValueAtTime(0.4, t + 0.42);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.82);
  osc.connect(filter).connect(gain).connect(c.destination);
  osc.start(t);
  lfo.start(t);
  osc.stop(t + 0.85);
  lfo.stop(t + 0.85);
}

function envelope(c, t, dur, peak) {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + Math.min(0.05, dur / 4));
  g.gain.setValueAtTime(peak, t + dur * 0.6);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  return g;
}

function voice(c, t, { points, dur, peak, filter = [900, 2000, 850], type = "sawtooth", q = 3 }) {
  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(points[0], t);
  points.slice(1).forEach((f, i) => osc.frequency.linearRampToValueAtTime(f, t + (dur * (i + 1)) / (points.length - 1)));
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = q;
  bp.frequency.setValueAtTime(filter[0], t);
  bp.frequency.linearRampToValueAtTime(filter[1], t + dur * 0.3);
  bp.frequency.linearRampToValueAtTime(filter[2], t + dur);
  osc.connect(bp).connect(envelope(c, t, dur, peak)).connect(c.destination);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

function noise(c, t, { dur, type, freq, peak, pulse }) {
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = freq;
  let node = src.connect(filter).connect(envelope(c, t, dur, peak));
  if (pulse) {
    const am = c.createGain();
    am.gain.value = 0.5;
    const lfo = c.createOscillator();
    const depth = c.createGain();
    lfo.frequency.value = pulse;
    depth.gain.value = 0.5;
    lfo.connect(depth).connect(am.gain);
    lfo.start(t);
    lfo.stop(t + dur);
    node = node.connect(am);
  }
  node.connect(c.destination);
  src.start(t);
  src.stop(t + dur);
}

const REACTION_SOUNDS = {
  love: (c, t) => noise(c, t, { dur: 1.1, type: "lowpass", freq: 320, peak: 0.9, pulse: 24 }),
  laugh: (c, t) => [0, 0.16, 0.32].forEach((d) => voice(c, t + d, { points: [780, 620], dur: 0.13, peak: 0.22 })),
  gasp: (c, t) => voice(c, t, { points: [480, 1150], dur: 0.28, peak: 0.25, filter: [1200, 2600, 2200] }),
  hiss: (c, t) => noise(c, t, { dur: 0.6, type: "highpass", freq: 2600, peak: 0.16 }),
  paw: (c, t) => voice(c, t, { points: [700, 240], dur: 0.14, peak: 0.3, type: "sine", filter: [600, 600, 400], q: 0.5 }),
  sleepy: (c, t) => voice(c, t, { points: [420, 540, 260], dur: 1.0, peak: 0.14, filter: [700, 1200, 500] }),
};

function react(kind) {
  const c = !muted && audio();
  if (c && REACTION_SOUNDS[kind]) REACTION_SOUNDS[kind](c, c.currentTime);
}

function tick() {
  const c = !muted && audio();
  if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "triangle";
  osc.frequency.value = 1500;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.1, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
  osc.connect(gain).connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.07);
}

export const sound = {
  meow,
  tick,
  react,
  unlock: () => audio(),
  setMuted(value) {
    muted = value;
    localStorage.setItem(KEY, value ? "1" : "0");
    listeners.forEach((fn) => fn());
  },
};

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useMuted = () => useSyncExternalStore(subscribe, () => muted);
